import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { startViewTransition } from '@theme/utilities';

// How long the swap waits for media the outgoing gallery never rendered. Past this the gallery
// changes anyway and the late image paints on its own, because a stalled swap reads as a freeze.
const MEDIA_READY_TIMEOUT = 600;

// Set on <html> for the length of the swap. custom.product.css hangs the "hold the page still"
// rule off it, so the transition only animates the tiles this file names.
const SWAP_CLASS = 'custom-media-swapping';

// The view-transition-class the named tiles carry, which is how custom.product.css times them
// without having to enumerate a name per slot.
const SWAP_CLASS_NAME = 'custom-media-swap';
const FOCUSABLE = 'button, a[href], input, select, textarea, [tabindex]';
const SURFACES = [
  '.media-gallery__grid',
  'slideshow-slides',
  '.dialog-zoomed-gallery',
  '.dialog-thumbnails-list',
  'slideshow-controls',
];
let activeSwaps = 0;
let swapSequence = 0;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#pendingSwap?.abort();
    this.#variantUpdateToken++;
  }

  /** Guards against a slow swap landing after a faster one the shopper asked for later. */
  #variantUpdateToken = 0;

  /** @type {AbortController | undefined} */
  #pendingSwap;

  #clearSwapNames = () => {};

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.#pendingSwap?.abort();
    this.#clearSwapNames();
    const controller = new AbortController();
    this.#pendingSwap = controller;
    const { signal } = controller;
    const token = ++this.#variantUpdateToken;
    const current = () => !signal.aborted && token === this.#variantUpdateToken && this.isConnected;

    await this.#awaitNewMedia(newMediaGallery, signal);
    if (!current()) return;

    const named = this.#nameChangedTiles(newMediaGallery);
    const clearNames = () => {
      for (const { tile, name } of named) {
        if (tile.style.getPropertyValue('view-transition-name') !== name) continue;
        tile.style.removeProperty('view-transition-name');
        tile.style.removeProperty('view-transition-class');
      }
    };
    this.#clearSwapNames = clearNames;
    const replace = () => {
      // A transition callback can run after another swatch response or a section removal.
      if (!current()) return;
      const restoreFocus = this.#focusRestorer(newMediaGallery);
      this.replaceWith(newMediaGallery);
      restoreFocus();
    };

    if (!named.length) {
      replace();
      return;
    }

    activeSwaps++;
    document.documentElement.classList.add(SWAP_CLASS);
    try {
      await startViewTransition(replace);
    } finally {
      if (--activeSwaps === 0) document.documentElement.classList.remove(SWAP_CLASS);
      clearNames();
    }
  };

  /**
   * @param {Element} newMediaGallery
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  #awaitNewMedia(newMediaGallery, signal) {
    const rendered = new Set(
      Array.from(this.querySelectorAll('img'), (image) => image.getAttribute('src'))
    );
    // The first copy has the gallery's responsive sizes; later copies include full-width zoom.
    /** @type {Map<string, HTMLImageElement>} */
    const sources = new Map();
    for (const image of newMediaGallery.querySelectorAll('img')) {
      const src = image.getAttribute('src');
      if (src && !rendered.has(src) && !sources.has(src)) sources.set(src, image);
    }
    const missing = [...sources.values()];
    if (!missing.length || signal.aborted) return Promise.resolve();

    return new Promise((resolve) => {
      const loaders = missing.map(() => new Image());
      let remaining = loaders.length;
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal.removeEventListener('abort', finish);
        for (const loader of loaders) {
          loader.removeAttribute('srcset');
          loader.removeAttribute('src');
        }
        resolve();
      };
      const timer = setTimeout(finish, MEDIA_READY_TIMEOUT);
      signal.addEventListener('abort', finish, { once: true });
      for (const [index, loader] of loaders.entries()) {
        const image = missing[index];
        if (!image) continue;
        // Set responsive candidates before src so the 3840px fallback never starts on its own.
        loader.sizes = image.sizes;
        loader.srcset = image.srcset;
        loader.src = image.src;
        loader
          .decode()
          .catch(() => undefined)
          .then(() => {
            if (--remaining === 0) finish();
          });
      }
    });
  }

  /**
   * A media id can occur in the grid, carousel and zoom. Each surface owns its own identity.
   * @param {Element} gallery
   * @returns {Map<string, HTMLElement>}
   */
  #tiles(gallery) {
    const tiles = new Map();
    for (const selector of SURFACES.slice(0, 3)) {
      gallery.querySelectorAll(selector).forEach((surface, index) => {
        for (const tile of surface.querySelectorAll('.product-media-container')) {
          const id = tile.querySelector('[data-media-id]')?.getAttribute('data-media-id');
          if (id && tile instanceof HTMLElement) tiles.set(`${selector}:${index}:${id}`, tile);
        }
      });
    }
    return tiles;
  }

  /**
   * @param {Element} newMediaGallery
   * @returns {{ tile: HTMLElement, name: string }[]}
   */
  #nameChangedTiles(newMediaGallery) {
    const swap = ++swapSequence;
    const oldTiles = this.#tiles(this);
    const newTiles = this.#tiles(newMediaGallery);
    /** @type {{ tile: HTMLElement, name: string }[]} */
    const named = [];
    let index = 0;
    /** @param {HTMLElement} tile @param {string} name */
    const nameTile = (tile, name) => {
      tile.style.setProperty('view-transition-name', name);
      tile.style.setProperty('view-transition-class', SWAP_CLASS_NAME);
      named.push({ tile, name });
    };
    for (const [key, oldTile] of oldTiles) {
      const newTile = newTiles.get(key);
      if (newTile) {
        newTiles.delete(key);
        if (
          oldTile.querySelector('img')?.getAttribute('src') ===
          newTile.querySelector('img')?.getAttribute('src')
        )
          continue;
      }
      const name = `custom-media-swap-${swap}-out-${index++}`;
      nameTile(oldTile, name);
      if (newTile) nameTile(newTile, name);
    }
    for (const tile of newTiles.values()) {
      nameTile(tile, `custom-media-swap-${swap}-in-${index++}`);
    }
    return named;
  }

  /**
   * Capture focus at replacement time, so decoding never pulls focus back from a swatch.
   * @param {Element} next
   * @returns {() => void}
   */
  #focusRestorer(next) {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) return () => {};
    const activeDialog = active.closest('dialog');
    const modalOpen = activeDialog?.open && this.contains(activeDialog);
    const selector = SURFACES.find((value) => active.closest(value));
    const oldSurface = selector ? active.closest(selector) : this;
    const surfaceIndex = selector
      ? [...this.querySelectorAll(selector)].indexOf(/** @type {Element} */ (oldSurface))
      : 0;
    const newSurface = (selector ? next.querySelectorAll(selector)[surfaceIndex] : next) ?? next;
    const oldTile = active.closest('.product-media-container, [data-media-id]');
    const mediaId =
      oldTile?.getAttribute('data-media-id') ??
      oldTile?.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    const media = mediaId
      ? newSurface.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`)
      : null;
    const newTile = media?.closest('.product-media-container') ?? media;
    const oldControls = oldTile
      ? [oldTile, ...oldTile.querySelectorAll(FOCUSABLE)].filter((node) => node.matches(FOCUSABLE))
      : [];
    const controlIndex = oldControls.indexOf(active);
    const newControls = newTile
      ? [newTile, ...newTile.querySelectorAll(FOCUSABLE)].filter((node) => node.matches(FOCUSABLE))
      : [];
    const ref = active.getAttribute('ref');
    const action = active.getAttribute('on:click');
    const equivalent =
      newControls[controlIndex] ??
      (active.id ? newSurface.querySelector(`#${CSS.escape(active.id)}`) : null) ??
      (!mediaId && ref ? newSurface.querySelector(`[ref="${CSS.escape(ref)}"]`) : null) ??
      (!mediaId && action
        ? [...newSurface.querySelectorAll(FOCUSABLE)].find(
            (node) => node.getAttribute('on:click') === action
          )
        : null);
    return () => {
      if (modalOpen) next.querySelector('dialog')?.showModal();
      const candidates = [
        equivalent,
        ...newControls,
        ...newSurface.querySelectorAll(FOCUSABLE),
        ...next.querySelectorAll(FOCUSABLE),
      ];
      for (const candidate of candidates) {
        if (
          !(candidate instanceof HTMLElement) ||
          !candidate.getClientRects().length ||
          candidate.matches(':disabled')
        )
          continue;
        candidate.focus({ preventScroll: true });
        if (document.activeElement === candidate) return;
      }
      if (next instanceof HTMLElement) {
        next.setAttribute('tabindex', '-1');
        next.focus({ preventScroll: true });
      }
    };
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
